import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Task } from '../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  ExternalLinkIcon,
} from '@pagespeedify/ui';



interface TaskDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
}

const TaskDetailsModal: React.FC<TaskDetailsModalProps> = ({ isOpen, onClose, task }) => {

  if (!task) return null;

  const priorityMap: { [key in Extract<Task['priority'], string>]: string } = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  };

  const typeMap: { [key in NonNullable<Task['type']>]: string } = {
    monitoring: 'Monitoring',
    log_analysis: 'Log Analysis',
    security: 'Security',
    custom: 'Custom',
    image_optimization: 'Image Optimization',
    agent: 'Agent',
  };

  // Helper to prettify image/file titles
  function prettifyFileLabel(file: string): string {
    if (!file) return '';
    if (file.startsWith('data:image')) return 'Embedded Image';
    try {
      const url = new URL(file);
      return url.pathname.split('/').pop() || url.hostname;
    } catch {
      // Not a valid URL, fallback to last part after /
      const parts = file.split('/');
      return parts[parts.length - 1] || file.slice(0, 30) + (file.length > 30 ? '...' : '');
    }
  }

  // Render context fields as digestible summary for users, not raw JSON
  function renderContextSummary(context: any) {
    if (!context || typeof context !== 'object') return null;
    switch (context.type) {
      case 'custom':
        if (context.contextDescription || context.affectedFiles) {
          return (
            <div className="mt-4 space-y-2">
              {context.contextDescription && (
                <div><strong>Notes:</strong> {context.contextDescription}</div>
              )}
              {context.affectedFiles && (
                <div><strong>Affected Files:</strong> {context.affectedFiles.split('\n').map((f: string, i: number) => <div key={i}>{f}</div>)}</div>
              )}
            </div>
          );
        }
        return null;
      case 'log_analysis':
        return (
          <div className="mt-4 space-y-2">
            {context.logType && <div><strong>Log Type:</strong> {context.logType}</div>}
            {context.dateRange && <div><strong>Date Range:</strong> {context.dateRange.from} to {context.dateRange.to}</div>}
            {context.errorPatterns && context.errorPatterns.length > 0 && <div><strong>Error Patterns:</strong> {context.errorPatterns.join(', ')}</div>}
          </div>
        );
      case 'security':
        return (
          <div className="mt-4 space-y-2">
            {context.vulnerability && <div><strong>Vulnerability:</strong> {context.vulnerability}</div>}
            {context.severity && <div><strong>Severity:</strong> {context.severity}</div>}
            {context.affectedComponents && context.affectedComponents.length > 0 && <div><strong>Affected Components:</strong> {context.affectedComponents.join(', ')}</div>}
          </div>
        );
      // --- NEW: Render context with items (e.g., audit/code issues, offscreen-images, generic, etc.) ---
      case 'offscreen-images':
      case 'unused-javascript':
      case 'generic':
      case 'audit':
        if (Array.isArray(context.items) && context.items.length > 0) {
          return (
            <div className="mt-4 space-y-2">
              <strong>{context.description || 'Details'}:</strong>
              <ul className="list-disc ml-6">
                {context.items.map((item: any, idx: number) => (
                  <li key={idx} className="mb-2">
                    {item.file && (item.file.startsWith('data:image') || item.file.match(/\.(png|jpe?g|gif|webp|svg)$/i)) ? (
                      <img src={item.file} alt={prettifyFileLabel(item.file)} style={{ maxWidth: '100%', maxHeight: 200, display: 'block', margin: '8px 0' }} />
                    ) : item.file ? (
                      <span>{prettifyFileLabel(item.file)}</span>
                    ) : null}
                    {item.formattedWasted && (
                      <span className="ml-2 text-xs text-muted-foreground">({item.formattedWasted})</span>
                    )}
                    {item.details && (
                      <div className="text-xs text-muted-foreground">{item.details}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        break;
      // --- fallback for unknown context types ---
      default:
        // If context has items, render them as a list
        if (Array.isArray(context.items) && context.items.length > 0) {
          return (
            <div className="mt-4 space-y-2">
              <strong>{context.description || 'Details'}:</strong>
              <ul className="list-disc ml-6">
                {context.items.map((item: any, idx: number) => (
                  <li key={idx} className="mb-2">
                    {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        // Otherwise, pretty-print the context as JSON (never render raw object)
        return (
          <div className="mt-4">
            <strong>Context Data:</strong>
            <pre className="bg-muted/30 p-2 rounded text-xs overflow-x-auto">{JSON.stringify(context, null, 2)}</pre>
          </div>
        );
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{prettifyFileLabel(task.title || '')}</DialogTitle>
          <DialogDescription>
            Detailed view of the task.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Task Details Section */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <h4 className="font-semibold mb-2">Status</h4>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.status === 'closed' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                {task.status}
              </span>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Priority</h4>
              <p>{typeof task.priority === 'number' ? task.priority : priorityMap[task.priority]}</p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Category</h4>
              <p>{task.type ? typeMap[task.type] : 'N/A'}</p>
            </div>
          </div>

          {task.description && (
            <div>
              <h4 className="font-semibold mb-2">Description</h4>
              <div className="prose prose-sm max-w-full overflow-x-auto">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{task.description}</ReactMarkdown>
              </div>
            </div>
          )}

          {task.notes && (
            <div>
              <h4 className="font-semibold mb-2">Notes</h4>
              <div className="prose prose-sm max-w-full overflow-x-auto">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{task.notes}</ReactMarkdown>
              </div>
            </div>
          )}

          {task.github_issue_url && (
            <div>
              <h4 className="font-semibold mb-2">GitHub Issue</h4>
              <a href={task.github_issue_url} target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-blue-600 hover:underline">
                <ExternalLinkIcon className="h-4 w-4 mr-1" />
                View Issue
              </a>
            </div>
          )}

          {/* Context Section */}
          {task.context && renderContextSummary(task.context)}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailsModal;
